import { useCallback } from "react";
import { useHubSpotAdapterAxiosInstance } from "./useHubSpotAdapterAxiosInstance.ts";

export type HistoryGranularity = "day" | "week" | "month";

export type HistorySlotStatus = "free" | "busy_work" | "busy_ooo";

export type HistorySlotCountsDTO = {
  free: number;
  inCall: number;
  ooo: number;
  effective: number;
};

export type HistorySlotTransitionsDTO = {
  booked: number;
  blocked: number;
  callBlocked: number;
  freed: number;
  callCancelled: number;
};

export type HistoryCloserSummaryDTO = {
  email: string;
  team: string | null;
  opening: HistorySlotCountsDTO | null;
  closure: HistorySlotCountsDTO | null;
};

export type HistoryPeriodSummaryDTO = {
  periodStart: string;
  periodEnd: string;
  openingTakenAt: string | null;
  closureTakenAt: string | null;
  closers: HistoryCloserSummaryDTO[];
};

export type HistoryPeriodsDTO = {
  teams: string[];
  periods: HistoryPeriodSummaryDTO[];
};

export type HistoryDayDTO = {
  date: string;
  opening: HistorySlotCountsDTO | null;
  closure: HistorySlotCountsDTO | null;
};

export type HistoryCloserSlotDTO = {
  slotStart: string;
  slotEnd: string;
  opening: HistorySlotStatus | null;
  closure: HistorySlotStatus | null;
  eventTitle: string | null;
};

export type HistoryCloserDetailDTO = HistoryCloserSummaryDTO & {
  days: HistoryDayDTO[] | null;
  transitions: HistorySlotTransitionsDTO | null;
  slots: HistoryCloserSlotDTO[] | null;
};

export type HistoryAddedBlockDTO = {
  email: string;
  date: string;
  slotStart: string;
  slotEnd: string;
  eventTitle: string | null;
  slots: number;
  calls: number;
};

export type HistoryPeriodDetailDTO = {
  teams: string[];
  periodStart: string;
  periodEnd: string;
  openingTakenAt: string | null;
  closureTakenAt: string | null;
  days: HistoryDayDTO[];
  closers: HistoryCloserDetailDTO[];
  addedBlocks: HistoryAddedBlockDTO[] | null;
};

export function useGetHubSpotCloserAvailabilityHistory() {
  const axiosInstance = useHubSpotAdapterAxiosInstance();

  const getHistoryPeriods = useCallback(
    async (
      granularity: HistoryGranularity,
      range?: { from: string; to: string },
    ) => {
      const path: Record<HistoryGranularity, string> = {
        day: "days",
        week: "weeks",
        month: "months",
      };
      const rangeParams: Record<HistoryGranularity, [string, string]> = {
        day: ["fromDay", "toDay"],
        week: ["fromWeek", "toWeek"],
        month: ["fromMonth", "toMonth"],
      };
      const [fromParam, toParam] = rangeParams[granularity];
      const query = range
        ? `?${fromParam}=${encodeURIComponent(range.from)}&${toParam}=${encodeURIComponent(range.to)}`
        : "";
      const { data } = await axiosInstance.get<HistoryPeriodsDTO>(
        `/closer-availability/history/${path[granularity]}${query}`,
      );
      return data;
    },
    [axiosInstance],
  );

  const getHistoryPeriodDetail = useCallback(
    async (granularity: HistoryGranularity, periodStart: string) => {
      const param = encodeURIComponent(periodStart);
      const path: Record<HistoryGranularity, string> = {
        day: `day?date=${param}`,
        week: `week?weekStart=${param}`,
        month: `month?month=${param}`,
      };
      const { data } = await axiosInstance.get<HistoryPeriodDetailDTO>(
        `/closer-availability/history/${path[granularity]}`,
      );
      return data;
    },
    [axiosInstance],
  );

  return { getHistoryPeriods, getHistoryPeriodDetail };
}
